import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const DRAWER_SELECTOR = '[data-cart-subscription-plan-drawer]';
const CLOSE_SELECTOR = '[data-cart-subscription-plan-close]';
const SCENARIO_SELECTOR = '[data-cart-subscription-plan-scenario]';
const ACTION_SELECTOR = '[data-cart-subscription-plan-action]';
const TITLE_SELECTOR = '[data-cart-subscription-plan-title]';
const ADD_ONE_TIME_ACTION = 'add-one-time';
const UPDATE_TO_90_ACTION = 'update-to-90';
const CHECKOUT_ACTION = 'checkout';
const ADD_ONE_TIME_BUTTON_SELECTOR = `[data-cart-subscription-plan-action="${ADD_ONE_TIME_ACTION}"]`;
const ADD_ONE_TIME_PRICE_SELECTOR = '[data-cart-subscription-plan-add-price]';
const ADD_ONE_TIME_COMPARE_SELECTOR = '[data-cart-subscription-plan-add-compare]';
const THIRTY_DAY_FREEBIES_SELECTOR = '[data-cart-subscription-plan-freebies]';
const ALLOWED_DRAWER_SCENARIOS = new Set(['added_90_subscription', 'added_30_subscription', 'added_one_time_purchase']);
const NINETY_DAY_PLAN_PATTERN = /\b90\b|\b3\s*month(s)?\b|\bquarter(ly)?\b/i;
const THIRTY_DAY_PLAN_PATTERN = /\b30\b|\b1\s*month(s)?\b|\bmonthly\b/i;
const CHECKOUT_URL = '/checkout';
const OPEN_DELAY_MS = 320;
const CLOSE_ANIMATION_MS = 220;
const CART_CLOSE_WAIT_MS = 900;
const BUTTON_ADDED_STATE_MS = 800;
const DEFERRED_CART_DRAWER_FLAG = '__macraDeferredCartDrawerOpen';

/** @type {number | undefined} */
let openTimeout;
/** @type {boolean} */
let isDrawerActionPending = false;
/** @type {{ item: any | null, line: number, sourceElement: Element | null }} */
let latestScenarioContext = {
  item: null,
  line: 0,
  sourceElement: null,
};
/** @type {boolean} */
let shouldOpenCartDrawerAfterClose = false;
/** @type {Map<string, {priceCents: number, compareCents: number}>} */
const variantPricingCache = new Map();
/** @type {Map<string, any>} */
const productDataCache = new Map();

/**
 * @returns {HTMLElement | null}
 */
function getDrawer() {
  const drawer = document.querySelector(DRAWER_SELECTOR);
  return drawer instanceof HTMLElement ? drawer : null;
}

/**
 * @param {HTMLElement | null} drawer
 */
function openDrawer(drawer) {
  if (!drawer) return;

  document.body.classList.add('overflow-hidden');
  drawer.hidden = false;
  requestAnimationFrame(() => {
    drawer.classList.add('is-open');
  });
}

function hasDeferredCartDrawerOpen() {
  return window[DEFERRED_CART_DRAWER_FLAG] === true;
}

function clearDeferredCartDrawerOpen() {
  try {
    delete window[DEFERRED_CART_DRAWER_FLAG];
  } catch (_) {
    window[DEFERRED_CART_DRAWER_FLAG] = false;
  }
}

function openDeferredCartDrawer() {
  clearDeferredCartDrawerOpen();
  openCartDrawer();
}

/**
 * @returns {boolean}
 */
function isProductDetailPage() {
  const templateName = `${window.Theme?.template?.name || ''}`.trim().toLowerCase();
  if (/^product([._/-].*)?$/.test(templateName)) return true;

  const body = document.body;
  if (!body) return false;

  return body.classList.contains('template-product') || body.classList.contains('template--product');
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isCartUpsellCartUpdateEvent(event) {
  const sourceElement = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  if (!(sourceElement instanceof HTMLElement)) return false;

  const sourceSectionId = `${sourceElement.dataset.sectionId || ''}`.trim().toLowerCase();
  if (sourceSectionId === 'cart-drawer') return true;

  return !!sourceElement.closest('.cart-upsell, .upsell_collection_block, [data-upsell-slider-root]');
}

/**
 * Allow drawer only for selected PDP ATC sources.
 * Currently allowed: Product Information ATC and Complementary Products ATC.
 * @param {Event} event
 * @returns {boolean}
 */
function isProductInformationCartUpdateEvent(event) {
  const sourceElement = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  if (!(sourceElement instanceof HTMLElement)) return false;

  if (sourceElement.closest('.product-information .buy-buttons-block')) return true;

  return false;
}

/**
 * @param {HTMLElement | null} drawer
 */
function closeDrawer(drawer) {
  if (!drawer) return;

  drawer.classList.remove('is-open');
  window.setTimeout(() => {
    if (!drawer.classList.contains('is-open')) {
      drawer.hidden = true;
      document.body.classList.remove('overflow-hidden');
    }
  }, CLOSE_ANIMATION_MS);
}

function closeSubscriptionDrawerAndOpenCart(drawer) {
  shouldOpenCartDrawerAfterClose = false;
  closeDrawer(drawer);
  window.setTimeout(() => {
    openDeferredCartDrawer();
  }, CLOSE_ANIMATION_MS);
}

/**
 * @param {HTMLElement} drawer
 * @param {string} scenario
 */
function setActiveScenario(drawer, scenario) {
  const sections = drawer.querySelectorAll(SCENARIO_SELECTOR);
  if (!sections.length) return;

  let matched = false;
  sections.forEach((section) => {
    if (!(section instanceof HTMLElement)) return;
    const isActive = section.dataset.cartSubscriptionPlanScenario === scenario;
    section.hidden = !isActive;
    if (isActive) matched = true;
  });

  if (!matched) {
    const fallback = sections[0];
    if (fallback instanceof HTMLElement) {
      fallback.hidden = false;
    }
  }
}

/**
 * @param {any} item
 * @param {string} preferredTitle
 * @returns {string}
 */
function getAddedProductTitle(item, preferredTitle = '') {
  const normalizedPreferredTitle = `${preferredTitle || ''}`.trim();
  if (normalizedPreferredTitle) return normalizedPreferredTitle;

  return `${item?.title || item?.product_title || ''}`.trim();
}

/**
 * @param {HTMLElement} drawer
 * @param {string} scenario
 * @param {any} item
 * @param {string} preferredTitle
 */
function updateDrawerTitle(drawer, scenario, item, preferredTitle = '') {
  const title = drawer.querySelector(TITLE_SELECTOR);
  if (!(title instanceof HTMLElement)) return;

  if (scenario === 'added_90_subscription') {
    if (window.matchMedia('(max-width: 749px)').matches) {
      title.textContent = 'BUNDLE & SAVE 50%';
      return;
    }
    const productTitle = getAddedProductTitle(item, preferredTitle);
    title.textContent = productTitle ? `${productTitle} successfully added to cart!` : 'Successfully added to cart!';
    return;
  }

  title.textContent = 'Upgrade & save';
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return `${value || ''}`
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * @param {HTMLElement} drawer
 * @param {string} scenario
 * @param {string} serializedFreebies
 */
function updateThirtyDayFreebies(drawer, scenario, serializedFreebies) {
  const container = drawer.querySelector(THIRTY_DAY_FREEBIES_SELECTOR);
  if (!(container instanceof HTMLElement)) return;

  if (scenario !== 'added_30_subscription') {
    container.hidden = true;
    return;
  }

  let freebies = [];
  try {
    freebies = JSON.parse(serializedFreebies || '[]');
  } catch (_) {
    freebies = [];
  }

  if (!Array.isArray(freebies) || !freebies.length) {
    container.innerHTML = '';
    container.hidden = true;
    return;
  }

  container.innerHTML = freebies
    .map((freebie) => {
      const title = escapeHtml(freebie?.title || '');
      const image = escapeHtml(freebie?.image || '');
      const valuePrice = escapeHtml(freebie?.valuePrice || '');
      if (!title || !image) return '';

      return `<div class="inlcude-product__block">
        <div class="inlcude-product__left">
          <div class="inlcude-product__image">
            <img src="${image}" width="400" height="400" alt="${title}" loading="lazy">
          </div>
          <div class="inlcude-product__title">
            <div class="title-container">
              <div class="title">
                <span>${title}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="inlcude-product__right">
          <div class="free-text">Free</div>
          <div class="price">${valuePrice}</div>
        </div>
      </div>`;
    })
    .filter(Boolean)
    .join('');

  container.hidden = !container.innerHTML;
}

/**
 * @param {HTMLButtonElement} button
 */
function triggerAddButtonAnimation(button) {
  button.dataset.added = 'true';
  window.setTimeout(() => {
    button.removeAttribute('data-added');
  }, BUTTON_ADDED_STATE_MS);
}

/**
 * @param {number} cents
 * @param {string} currency
 * @returns {string}
 */
function formatMoneyFromCents(cents, currency) {
  if (!Number.isFinite(cents)) return '';

  const amount = cents / 100;
  const normalizedAmount = Number.isInteger(amount) ? `${amount}` : `${amount.toFixed(2)}`.replace(/0+$/, '').replace(/\.$/, '');
  const fallback = `$${normalizedAmount}`;
  const resolvedCurrency = `${currency || ''}`.trim().toUpperCase();

  // Keep drawer price format consistent with theme liquid money_without_trailing_zeros output
  // for this storefront requirement: "$79" instead of "US$79.00".
  if (!resolvedCurrency || resolvedCurrency === 'USD') {
    return fallback;
  }

  try {
    const formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: resolvedCurrency,
    }).format(amount);

    return formatted.replace(/\.00(?=\D*$)/, '');
  } catch (_) {
    return fallback;
  }
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function parsePositiveNumber(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) return 0;
  return numberValue;
}

/**
 * @param {any} item
 * @returns {{priceCents: number, compareCents: number}}
 */
function getFallbackPricing(item) {
  const priceCents = parsePositiveNumber(item?.original_price) || parsePositiveNumber(item?.price) || parsePositiveNumber(item?.final_price);
  const compareCandidate = parsePositiveNumber(item?.price);
  const compareCents = compareCandidate > priceCents ? compareCandidate : 0;

  return {
    priceCents,
    compareCents,
  };
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function extractDayCount(value) {
  const normalizedValue = `${value || ''}`.trim().replace(/[,-]/g, ' ');
  if (!normalizedValue) return 0;

  const dayMatch = normalizedValue.match(/\b(\d{1,3})\b/);
  return dayMatch ? Number(dayMatch[1]) : 0;
}

/**
 * @param {unknown} value
 * @param {number} targetDays
 * @returns {boolean}
 */
function isTargetDayMatch(value, targetDays) {
  const dayCount = extractDayCount(value);
  if (dayCount === targetDays) return true;
  if (targetDays === 90) return NINETY_DAY_PLAN_PATTERN.test(`${value || ''}`);
  if (targetDays === 30) return THIRTY_DAY_PLAN_PATTERN.test(`${value || ''}`);
  return false;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isNinetyDayMatch(value) {
  return isTargetDayMatch(value, 90);
}

/**
 * @param {HTMLElement} comparison
 * @param {number} [targetDays]
 * @returns {{variantId: string, sellingPlanId: string, isAvailable: boolean}}
 */
function getNinetyDayTargetFromComparisonElement(comparison, targetDays = 90) {
  const targetCard = comparison.querySelector(
    `.subscription-plan-comparison__plan-container[data-total-days="${targetDays}"] [data-plan-card]`
  );
  if (!(targetCard instanceof HTMLElement)) {
    return { variantId: '', sellingPlanId: '', isAvailable: true };
  }

  const variantAvailableRaw = `${targetCard.dataset.variantAvailable || ''}`.trim().toLowerCase();
  const isAvailable = variantAvailableRaw ? variantAvailableRaw !== 'false' && variantAvailableRaw !== '0' : true;

  return {
    variantId: `${targetCard.dataset.variantId || ''}`.trim(),
    sellingPlanId: `${targetCard.dataset.sellingPlanId || ''}`.trim(),
    isAvailable,
  };
}

/**
 * @param {Element | null} sourceElement
 * @param {string} selector
 * @returns {HTMLElement[]}
 */
function getScopedComparisonCandidates(sourceElement, selector) {
  if (!(sourceElement instanceof Element)) return [];

  const scope = sourceElement.closest('.shopify-section, dialog, product-card, [id*="ProductInformation-"], [id*="QuickAdd-"]');
  if (!(scope instanceof Element)) return [];

  return Array.from(scope.querySelectorAll(selector)).filter((element) => element instanceof HTMLElement);
}

/**
 * @param {any} item
 * @param {Element | null} [sourceElement]
 * @param {number} [targetDays]
 * @returns {{variantId: string, sellingPlanId: string, isAvailable: boolean}}
 */
function resolveNinetyDayTargetFromComparison(item, sourceElement = null, targetDays = 90) {
  const productId = `${item?.product_id || ''}`.trim();
  const selector = productId
    ? `subscription-plan-comparison-component[data-product-id="${productId}"]`
    : 'subscription-plan-comparison-component';

  const scopedComparisons = getScopedComparisonCandidates(sourceElement, selector);
  const globalComparisons = Array.from(document.querySelectorAll(selector)).filter(
    (element) => element instanceof HTMLElement
  );
  const comparisons = [...scopedComparisons];
  globalComparisons.forEach((comparison) => {
    if (!comparisons.includes(comparison)) {
      comparisons.push(comparison);
    }
  });

  if (!comparisons.length) {
    return { variantId: '', sellingPlanId: '', isAvailable: true };
  }

  const currentVariantId = `${item?.variant_id || ''}`.trim();
  const currentSellingPlanId = getSellingPlanId(item);

  // Prefer the comparison block that actually contains the current cart item pairing.
  if (currentVariantId || currentSellingPlanId) {
    for (const comparison of comparisons) {
      const cards = Array.from(comparison.querySelectorAll('[data-plan-card]'));
      const matchesCurrentItem = cards.some((card) => {
        if (!(card instanceof HTMLElement)) return false;

        const cardVariantId = `${card.dataset.variantId || ''}`.trim();
        const cardSellingPlanId = `${card.dataset.sellingPlanId || ''}`.trim();

        if (currentVariantId && currentSellingPlanId) {
          return cardVariantId === currentVariantId && cardSellingPlanId === currentSellingPlanId;
        }
        if (currentVariantId) return cardVariantId === currentVariantId;
        return cardSellingPlanId === currentSellingPlanId;
      });

      if (!matchesCurrentItem) continue;

      const matchedTarget = getNinetyDayTargetFromComparisonElement(comparison, targetDays);
      if (matchedTarget.variantId || matchedTarget.sellingPlanId) {
        return matchedTarget;
      }
    }
  }

  // Fallback: first block with a valid 90-day target.
  for (const comparison of comparisons) {
    const target = getNinetyDayTargetFromComparisonElement(comparison, targetDays);
    if (target.variantId || target.sellingPlanId) {
      return target;
    }
  }

  return { variantId: '', sellingPlanId: '', isAvailable: true };
}

/**
 * @param {any} variant
 * @param {number} [targetDays]
 * @returns {boolean}
 */
function isNinetyDayVariant(variant, targetDays = 90) {
  const candidateValues = [
    variant?.title,
    variant?.name,
    variant?.option1,
    variant?.option2,
    variant?.option3,
  ];

  if (Array.isArray(variant?.options)) {
    candidateValues.push(...variant.options);
  }

  return candidateValues.some((entry) => isTargetDayMatch(entry, targetDays));
}

/**
 * @param {any} variant
 * @returns {{priceCents: number, compareCents: number}}
 */
function getVariantPriceSnapshot(variant) {
  const priceCents = parsePositiveNumber(variant?.price);
  const compareCandidate = parsePositiveNumber(variant?.compare_at_price);
  const compareCents = compareCandidate > priceCents ? compareCandidate : 0;

  return {
    priceCents,
    compareCents,
  };
}

/**
 * @param {string} handle
 * @returns {Promise<any | null>}
 */
async function getProductData(handle) {
  const normalizedHandle = `${handle || ''}`.trim();
  if (!normalizedHandle) return null;

  const cachedProduct = productDataCache.get(normalizedHandle);
  if (cachedProduct) return cachedProduct;

  try {
    const response = await fetch(`/products/${encodeURIComponent(normalizedHandle)}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });
    if (!response.ok) return null;

    const product = await response.json();
    productDataCache.set(normalizedHandle, product);
    return product;
  } catch (error) {
    console.error('Unable to resolve product data for subscription drawer:', error);
    return null;
  }
}

/**
 * @param {any} item
 * @param {string} [explicitVariantId]
 * @returns {Promise<{priceCents: number, compareCents: number} | null>}
 */
async function getVariantPricing(item, explicitVariantId = '') {
  const handle = `${item?.handle || ''}`.trim();
  const variantId = `${explicitVariantId || item?.variant_id || ''}`.trim();
  if (!handle || !variantId) return null;

  const cacheKey = `${handle}:${variantId}`;
  const cachedValue = variantPricingCache.get(cacheKey);
  if (cachedValue) return cachedValue;

  try {
    const product = await getProductData(handle);
    if (!product) return null;
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant = variants.find((entry) => `${entry?.id || ''}`.trim() === variantId);
    if (!variant) return null;

    const pricing = getVariantPriceSnapshot(variant);
    variantPricingCache.set(cacheKey, pricing);
    return pricing;
  } catch (error) {
    console.error('Unable to resolve variant pricing for subscription drawer:', error);
    return null;
  }
}

/**
 * @param {any} item
 * @param {number} [targetDays]
 * @returns {Promise<string>}
 */
async function resolveNinetyDayVariantId(item, targetDays = 90) {
  const currentVariantId = `${item?.variant_id || ''}`.trim();
  const handle = `${item?.handle || ''}`.trim();
  if (!handle) return currentVariantId;

  const product = await getProductData(handle);
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (!variants.length) return currentVariantId;

  const ninetyDayVariant = variants.find((variant) => isNinetyDayVariant(variant, targetDays));
  if (!ninetyDayVariant) return currentVariantId;

  return `${ninetyDayVariant.id || ''}`.trim() || currentVariantId;
}

/**
 * @param {any} item
 * @returns {Promise<{variantId: string, pricing: {priceCents: number, compareCents: number} | null}>}
 */
async function resolveFirstVariantPricing(item) {
  const handle = `${item?.handle || ''}`.trim();
  if (!handle) {
    return {
      variantId: '',
      pricing: null,
    };
  }

  const product = await getProductData(handle);
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const firstVariant = variants[0];
  const firstVariantId = `${firstVariant?.id || ''}`.trim();

  if (!firstVariantId) {
    return {
      variantId: '',
      pricing: null,
    };
  }

  const pricing = getVariantPriceSnapshot(firstVariant);
  variantPricingCache.set(`${handle}:${firstVariantId}`, pricing);

  return {
    variantId: firstVariantId,
    pricing,
  };
}

/**
 * @param {HTMLElement} drawer
 * @param {{priceCents: number, compareCents: number}} pricing
 * @param {string} currency
 * @param {boolean} isVisible
 */
function applyNinetyDayPricing(drawer, pricing, currency, isVisible) {
  const priceElement = drawer.querySelector(ADD_ONE_TIME_PRICE_SELECTOR);
  if (priceElement instanceof HTMLElement) {
    if (isVisible && pricing.priceCents > 0) {
      priceElement.textContent = formatMoneyFromCents(pricing.priceCents, currency);
      priceElement.hidden = false;
    } else {
      priceElement.textContent = '';
      priceElement.hidden = true;
    }
  }

  const compareElement = drawer.querySelector(ADD_ONE_TIME_COMPARE_SELECTOR);
  if (compareElement instanceof HTMLElement) {
    if (isVisible && pricing.compareCents > 0) {
      compareElement.textContent = formatMoneyFromCents(pricing.compareCents, currency);
      compareElement.hidden = false;
    } else {
      compareElement.textContent = '';
      compareElement.hidden = true;
    }
  }
}

/**
 * @param {HTMLElement} drawer
 * @param {string} scenario
 * @param {any} item
 * @param {string} currency
 */
async function updateNinetyDayActions(drawer, scenario, item, currency) {
  const addButton = drawer.querySelector(ADD_ONE_TIME_BUTTON_SELECTOR);
  if (!(addButton instanceof HTMLButtonElement)) return;

  const isNinetyDayScenario = scenario === 'added_90_subscription';
  const configuredVariantId = `${addButton.dataset.configuredVariantId || ''}`.trim();
  const addedVariantId = `${item?.variant_id || ''}`.trim();
  let actionVariantId = configuredVariantId || addedVariantId;
  let pricing = null;

  if (isNinetyDayScenario && !configuredVariantId) {
    const firstVariantData = await resolveFirstVariantPricing(item);
    if (firstVariantData.variantId) {
      actionVariantId = firstVariantData.variantId;
      pricing = firstVariantData.pricing;
    }
  }

  addButton.dataset.variantId = actionVariantId;
  addButton.disabled = !isNinetyDayScenario || !actionVariantId || isDrawerActionPending;

  if (!isNinetyDayScenario || !actionVariantId) {
    applyNinetyDayPricing(drawer, { priceCents: 0, compareCents: 0 }, currency, false);
    return;
  }

  // When Liquid provides an explicit drawer target product/variant, keep Liquid-rendered pricing.
  if (configuredVariantId) {
    const configuredPrice = `${addButton.dataset.configuredPrice || ''}`.trim();
    const configuredCompare = `${addButton.dataset.configuredCompare || ''}`.trim();

    const priceElement = drawer.querySelector(ADD_ONE_TIME_PRICE_SELECTOR);
    if (priceElement instanceof HTMLElement) {
      priceElement.textContent = configuredPrice;
      priceElement.hidden = !configuredPrice;
    }

    const compareElement = drawer.querySelector(ADD_ONE_TIME_COMPARE_SELECTOR);
    if (compareElement instanceof HTMLElement) {
      compareElement.textContent = configuredCompare;
      compareElement.hidden = !configuredCompare;
    }

    return;
  }

  const variantPricing = pricing || (await getVariantPricing(item, actionVariantId));
  pricing = variantPricing || getFallbackPricing(item);
  applyNinetyDayPricing(drawer, pricing, currency, true);
}

/**
 * @param {any} item
 * @returns {'added_90_subscription' | 'added_30_subscription' | 'added_one_time_purchase' | null}
 */
function resolveScenarioFromItem(item) {
  const planName = `${item?.selling_plan_allocation?.selling_plan?.name || ''}`.trim();
  if (!planName) return 'added_one_time_purchase';

  const normalizedPlanName = planName.replace(/[,-]/g, ' ');
  const dayMatch = normalizedPlanName.match(/\b(\d{1,3})\b/);
  const days = dayMatch ? Number(dayMatch[1]) : 0;

  if (days === 90) return 'added_90_subscription';
  if (days === 30) return 'added_30_subscription';
  return null;
}

/**
 * @param {HTMLElement | null} drawer
 * @param {'added_90_subscription' | 'added_30_subscription' | 'added_one_time_purchase' | null} scenario
 * @returns {boolean}
 */
function isAllowedDrawerScenario(drawer, scenario) {
  if (!scenario || !ALLOWED_DRAWER_SCENARIOS.has(scenario)) return false;
  if (!(drawer instanceof HTMLElement)) return false;

  const scenarioElement = drawer.querySelector(
    `${SCENARIO_SELECTOR}[data-cart-subscription-plan-scenario="${scenario}"]`
  );
  if (!(scenarioElement instanceof HTMLElement)) return false;

  return `${scenarioElement.dataset.cartSubscriptionPlanEnabled || ''}`.trim() === 'true';
}

/**
 * @param {any[]} items
 * @param {(item: any) => boolean} predicate
 * @returns {any | null}
 */
function findLastMatchingItem(items, predicate) {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (predicate(item)) return item;
  }

  return null;
}

/**
 * @param {any} item
 * @returns {string}
 */
function getSellingPlanId(item) {
  return `${item?.selling_plan_allocation?.selling_plan?.id || ''}`.trim();
}

/**
 * @param {any[]} items
 * @param {string} productId
 * @returns {boolean}
 */
function hasNinetyDaySubscriptionForProduct(items, productId) {
  const normalizedProductId = `${productId || ''}`.trim();
  if (!normalizedProductId) return false;

  return items.some((item) => {
    if (`${item?.product_id || ''}`.trim() !== normalizedProductId) return false;
    return resolveScenarioFromItem(item) === 'added_90_subscription';
  });
}

/**
 * @param {{ lineItemKey?: string, variantId?: string, productId?: string, selectedSellingPlanId?: string, addedQuantity?: number }} matcher
 * @returns {Promise<{scenario: 'added_90_subscription' | 'added_30_subscription' | 'added_one_time_purchase' | null, item: any | null, line: number, currency: string, productHasNinetyDaySubscription: boolean}>}
 */
async function getScenarioFromCart(matcher) {
  try {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      return {
        scenario: null,
        item: null,
        line: 0,
        currency: '',
        productHasNinetyDaySubscription: false,
      };
    }

    const cart = await response.json();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) {
      return {
        scenario: null,
        item: null,
        line: 0,
        currency: `${cart?.currency || ''}`.trim(),
        productHasNinetyDaySubscription: false,
      };
    }

    const lineItemKey = `${matcher?.lineItemKey || ''}`.trim();
    const variantId = `${matcher?.variantId || ''}`.trim();
    const productId = `${matcher?.productId || ''}`.trim();
    const selectedSellingPlanId = `${matcher?.selectedSellingPlanId || ''}`.trim();
    const addedQuantity = Math.max(1, Number(matcher?.addedQuantity || 1));

    let addedItem = null;

    if (lineItemKey) {
      addedItem = findLastMatchingItem(items, (item) => `${item?.key || ''}`.trim() === lineItemKey);
    }

    if (!addedItem && variantId && selectedSellingPlanId) {
      addedItem = findLastMatchingItem(
        items,
        (item) => `${item?.variant_id || ''}`.trim() === variantId && getSellingPlanId(item) === selectedSellingPlanId
      );
    }

    if (!addedItem && variantId && !selectedSellingPlanId) {
      addedItem = findLastMatchingItem(
        items,
        (item) => `${item?.variant_id || ''}`.trim() === variantId && !getSellingPlanId(item)
      );
    }

    if (!addedItem && variantId) {
      addedItem = findLastMatchingItem(items, (item) => `${item?.variant_id || ''}`.trim() === variantId);
    }

    if (!addedItem && productId && selectedSellingPlanId) {
      addedItem = findLastMatchingItem(
        items,
        (item) => `${item?.product_id || ''}`.trim() === productId && getSellingPlanId(item) === selectedSellingPlanId
      );
    }

    if (!addedItem && productId && !selectedSellingPlanId) {
      addedItem = findLastMatchingItem(
        items,
        (item) => `${item?.product_id || ''}`.trim() === productId && !getSellingPlanId(item)
      );
    }

    if (!addedItem && productId) {
      addedItem = findLastMatchingItem(items, (item) => `${item?.product_id || ''}`.trim() === productId);
    }

    if (!addedItem) {
      addedItem = items[items.length - 1];
    }

    const line = items.indexOf(addedItem) + 1;
    const resolvedProductId = `${addedItem?.product_id || productId || ''}`.trim();
    const scenario = resolveScenarioFromItem(addedItem);
    const productHasNinetyDaySubscription = hasNinetyDaySubscriptionForProduct(items, resolvedProductId);
    const addedItemKey = `${addedItem?.key || ''}`.trim();
    const wasExistingNinetyDayLine =
      scenario === 'added_90_subscription' &&
      (lineItemKey ? lineItemKey === addedItemKey : true) &&
      Math.max(0, Number(addedItem?.quantity || 0) - addedQuantity) > 0;
    const shouldSuppressDrawerForProduct = productHasNinetyDaySubscription && !(
      scenario === 'added_90_subscription' && !wasExistingNinetyDayLine
    );

    return {
      scenario,
      item: addedItem,
      line: line > 0 ? line : 0,
      currency: `${cart?.currency || ''}`.trim(),
      productHasNinetyDaySubscription: shouldSuppressDrawerForProduct,
    };
  } catch (error) {
    console.error('Unable to resolve subscription drawer scenario:', error);
    return {
      scenario: null,
      item: null,
      line: 0,
      currency: '',
      productHasNinetyDaySubscription: false,
    };
  }
}

/**
 * @returns {HTMLElement | null}
 */
function getCartDrawerComponent() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  return cartDrawer instanceof HTMLElement ? cartDrawer : null;
}

/**
 * @param {HTMLDialogElement} dialog
 * @returns {Promise<void>}
 */
function waitForDialogClose(dialog) {
  if (!dialog.open) return Promise.resolve();

  return new Promise((resolve) => {
    const startedAt = Date.now();

    const poll = () => {
      if (!dialog.open || Date.now() - startedAt > CART_CLOSE_WAIT_MS) {
        resolve();
        return;
      }

      window.setTimeout(poll, 30);
    };

    poll();
  });
}

function openCartDrawer() {
  const cartDrawerComponent = getCartDrawerComponent();
  if (!cartDrawerComponent) return;

  if ('open' in cartDrawerComponent && typeof cartDrawerComponent.open === 'function') {
    cartDrawerComponent.open();
    return;
  }

  if ('showDialog' in cartDrawerComponent && typeof cartDrawerComponent.showDialog === 'function') {
    cartDrawerComponent.showDialog();
  }
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  const sections = new Set();
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sections.add(item.dataset.sectionId);
    }
  });

  return Array.from(sections);
}

/**
 * @param {Object} config
 * @param {number} config.line
 * @param {number} config.quantity
 * @param {string} [config.lineItemKey]
 * @param {string} [config.sellingPlanId]
 * @returns {Promise<any>}
 */
async function updateCartLineItem(config) {
  const { line, quantity, lineItemKey = '', sellingPlanId = '' } = config;
  const payload = {
    quantity,
    selling_plan: sellingPlanId || null,
    sections: getCartSectionIds().join(','),
    sections_url: window.location.pathname,
  };

  if (lineItemKey) {
    payload.id = lineItemKey;
  } else {
    payload.line = line;
  }

  const body = JSON.stringify(payload);

  const changeResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
  const changeResponseText = await changeResponse.text();
  const parsedResponse = JSON.parse(changeResponseText);

  if (!changeResponse.ok || parsedResponse?.errors) {
    throw new Error(parsedResponse?.errors || 'Unable to update cart line.');
  }

  return parsedResponse;
}

/**
 * @param {Object} config
 * @param {string} config.variantId
 * @param {number} config.quantity
 * @param {string} config.sellingPlanId
 * @returns {Promise<any>}
 */
async function addSubscriptionLineItem(config) {
  const { variantId, quantity, sellingPlanId } = config;
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', `${quantity || 1}`);
  formData.append('selling_plan', sellingPlanId);

  const sectionIds = getCartSectionIds();
  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
  }

  const fetchCfg = fetchConfig('javascript', { body: formData });
  const addResponse = await fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: {
      ...fetchCfg.headers,
      Accept: 'application/json',
    },
  });
  const addData = await addResponse.json();

  if (!addResponse.ok || addData?.status) {
    throw new Error(addData?.description || addData?.message || 'Unable to add 90 day subscription.');
  }

  return addData;
}

/**
 * @param {any} item
 * @param {number} line
 * @returns {boolean}
 */
function itemSupportsSubscription(item, line) {
  if (item?.selling_plan_allocation) return true;

  if (line > 0) {
    const selectorByLine = document.querySelector(`cart-selling-plan-selector[data-line="${line}"]`);
    if (selectorByLine instanceof HTMLElement) {
      return true;
    }
  }

  const key = `${item?.key || ''}`.trim();
  if (!key) return false;

  const selectorByKey = document.querySelector(`.cart-items__table-row[data-key="${key}"] cart-selling-plan-selector`);
  return selectorByKey instanceof HTMLElement;
}

/**
 * @param {number} line
 * @param {any} item
 * @returns {HTMLSelectElement | null}
 */
function getSellingPlanSelectForItem(line, item) {
  if (line > 0) {
    const selectorByLine = document.querySelector(`cart-selling-plan-selector[data-line="${line}"] [data-selling-plan-select]`);
    if (selectorByLine instanceof HTMLSelectElement) {
      return selectorByLine;
    }
  }

  const lineItemKey = `${item?.key || ''}`.trim();
  if (!lineItemKey) return null;

  const rows = document.querySelectorAll('.cart-items__table-row[data-key]');
  for (const row of rows) {
    if (!(row instanceof HTMLElement)) continue;
    if (`${row.dataset.key || ''}`.trim() !== lineItemKey) continue;

    const selector = row.querySelector('cart-selling-plan-selector [data-selling-plan-select]');
    if (selector instanceof HTMLSelectElement) {
      return selector;
    }
  }

  return null;
}

/**
 * @param {number} line
 * @param {any} item
 * @param {number} [targetDays]
 * @returns {string}
 */
function resolveNinetyDaySellingPlanId(line, item, targetDays = 90) {
  const selector = getSellingPlanSelectForItem(line, item);
  if (!(selector instanceof HTMLSelectElement)) return '';

  const options = Array.from(selector.options);
  for (const option of options) {
    const value = `${option.value || ''}`.trim();
    if (!value) continue;

    const label = `${option.textContent || ''}`.trim();
    if (isTargetDayMatch(label, targetDays)) {
      return value;
    }
  }

  return '';
}

/**
 * @param {HTMLElement} drawer
 * @param {string} scenario
 * @param {any} item
 * @param {number} line
 */
function updateUpgradeToNinetyActions(drawer, scenario, item, line) {
  const buttons = drawer.querySelectorAll(`[data-cart-subscription-plan-action="${UPDATE_TO_90_ACTION}"]`);
  if (!buttons.length) return;

  const targetDays = (scenario === 'added_30_subscription' || scenario === 'added_one_time_purchase') ? 90 : 30;
  const canUpgrade = (scenario === 'added_30_subscription' || scenario === 'added_one_time_purchase') && !!item && line > 0;
  const ninetyDayPlanId = canUpgrade ? resolveNinetyDaySellingPlanId(line, item, targetDays) : '';
  const ninetyDayTarget = canUpgrade
    ? resolveNinetyDayTargetFromComparison(item, latestScenarioContext.sourceElement, targetDays)
    : { variantId: '', sellingPlanId: '', isAvailable: true };
  const targetSellingPlanId = ninetyDayTarget.sellingPlanId || ninetyDayPlanId;
  const isTargetInStock = ninetyDayTarget.isAvailable !== false;
  const canSubmitUpgrade = canUpgrade && !!targetSellingPlanId && isTargetInStock;

  buttons.forEach((element) => {
    if (!(element instanceof HTMLButtonElement)) return;

    const label = element.querySelector('[data-cart-subscription-plan-update-text]');
    const defaultLabel = `${element.dataset.defaultLabel || label?.textContent || 'Upgrade my subscription'}`.trim();
    element.dataset.defaultLabel = defaultLabel;

    if (label instanceof HTMLElement) {
      label.textContent = canUpgrade && !isTargetInStock ? 'Out of stock' : defaultLabel;
    }

    element.dataset.line = `${line || 0}`;
    element.dataset.targetDays = `${targetDays}`;
    element.dataset.ninetyDayPlanId = targetSellingPlanId;
    element.dataset.ninetyDayVariantId = `${ninetyDayTarget.variantId || ''}`.trim();
    element.dataset.upgradeAvailable = canSubmitUpgrade ? 'true' : 'false';
    element.disabled = !canSubmitUpgrade || isDrawerActionPending;
  });
}

/**
 * @param {HTMLButtonElement} button
 */
async function updateToNinetyDaySubscription(button) {
  if (isDrawerActionPending) return;
  if (button.dataset.upgradeAvailable === 'false') return;

  const drawer = getDrawer();
  const line = Number(button.dataset.line || latestScenarioContext.line || 0);
  const item = latestScenarioContext.item;
  const targetDays = Number(button.dataset.targetDays || 90);
  const currentLineItemKey = `${item?.key || ''}`.trim();
  const quantity = Math.max(1, Number(item?.quantity || 1));
  const comparisonTarget = resolveNinetyDayTargetFromComparison(item, latestScenarioContext.sourceElement, targetDays);
  const currentVariantId = `${item?.variant_id || ''}`.trim();
  const fallbackVariantId = (await resolveNinetyDayVariantId(item, targetDays)).trim();
  const fallbackPlanId = resolveNinetyDaySellingPlanId(line, item, targetDays).trim();
  const upgradeCandidates = [
    {
      variantId: `${button.dataset.ninetyDayVariantId || ''}`.trim(),
      sellingPlanId: `${button.dataset.ninetyDayPlanId || ''}`.trim(),
    },
    {
      variantId: `${comparisonTarget.variantId || ''}`.trim(),
      sellingPlanId: `${comparisonTarget.sellingPlanId || ''}`.trim(),
    },
    {
      variantId: `${comparisonTarget.variantId || ''}`.trim(),
      sellingPlanId: fallbackPlanId,
    },
    {
      variantId: fallbackVariantId,
      sellingPlanId: fallbackPlanId,
    },
  ].filter((candidate, index, list) => {
    if (!candidate.variantId || !candidate.sellingPlanId) return false;
    return (
      list.findIndex(
        (entry) => entry.variantId === candidate.variantId && entry.sellingPlanId === candidate.sellingPlanId
      ) === index
    );
  });

  if (!line || !item) return;
  if (!upgradeCandidates.length) {
    console.error(`Unable to find ${targetDays} day selling plan for the selected cart line.`);
    return;
  }

  isDrawerActionPending = true;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  triggerAddButtonAnimation(button);

  try {
    let parsedResponse = null;
    let lastError = null;

    for (const candidate of upgradeCandidates) {
      let addSucceeded = false;
      try {
        if (candidate.variantId !== currentVariantId) {
          await addSubscriptionLineItem({
            variantId: candidate.variantId,
            quantity,
            sellingPlanId: candidate.sellingPlanId,
          });
          addSucceeded = true;
          parsedResponse = await updateCartLineItem({
            line,
            lineItemKey: currentLineItemKey,
            quantity: 0,
          });
        } else {
          parsedResponse = await updateCartLineItem({
            line,
            quantity,
            sellingPlanId: candidate.sellingPlanId,
          });
        }

        if (parsedResponse) break;
      } catch (error) {
        lastError = error;
        // If add succeeded but remove failed, don't retry to avoid duplicate adds.
        if (addSucceeded) {
          throw error;
        }
      }
    }

    if (!parsedResponse) {
      throw lastError || new Error(`Unable to upgrade subscription to ${targetDays} day.`);
    }

    document.dispatchEvent(
      new CartUpdateEvent(parsedResponse, 'cart-subscription-plan-drawer', {
        source: 'cart-subscription-plan-drawer',
        itemCount: Number(parsedResponse?.item_count) || 0,
        sections: parsedResponse?.sections,
      })
    );

    closeSubscriptionDrawerAndOpenCart(drawer);
  } catch (error) {
    console.error(`Unable to upgrade subscription to ${targetDays} day:`, error);
  } finally {
    isDrawerActionPending = false;
    button.removeAttribute('aria-busy');
    if (drawer && !drawer.hidden) {
      button.disabled = button.dataset.upgradeAvailable !== 'true';
    }
  }
}

async function addOneTimePurchase(button) {
  const variantId = `${button.dataset.variantId || ''}`.trim();
  if (!variantId || isDrawerActionPending) return;

  isDrawerActionPending = true;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  triggerAddButtonAnimation(button);
  const drawer = getDrawer();

  try {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = getCartSectionIds();
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const addResponse = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });
    const addData = await addResponse.json();

    if (!addResponse.ok || addData?.status) {
      throw new Error(addData?.description || addData?.message || 'Unable to add one-time purchase.');
    }

    const cartResponse = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });
    if (!cartResponse.ok) {
      throw new Error('Unable to refresh cart state.');
    }
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartAddEvent(cart, variantId, {
        source: 'cart-subscription-plan-drawer',
        itemCount: 1,
        variantId,
        lineItemKey: `${addData?.key || ''}`,
        selectedSellingPlanId: '',
        animateLikeProductForm: true,
        sections: addData?.sections,
      })
    );

    closeSubscriptionDrawerAndOpenCart(drawer);
  } catch (error) {
    console.error('Unable to add one-time purchase from subscription drawer:', error);
  } finally {
    isDrawerActionPending = false;
    button.removeAttribute('aria-busy');
    if (drawer && !drawer.hidden) {
      button.disabled = false;
    }
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (!isProductDetailPage()) return;
  if (isCartUpsellCartUpdateEvent(event)) return;
  if (!isProductInformationCartUpdateEvent(event)) return;

  const data = event.detail?.data;
  if (!data || data.didError || data.source !== 'product-form-component') return;

  const lineItemKey = `${data.lineItemKey || ''}`.trim();
  const variantId = `${data.variantId || ''}`.trim();
  const productId = `${data.productId || ''}`.trim();
  const productCustomTitle = `${data.productCustomTitle || ''}`.trim();
  const productNinetyDayFreebies = `${data.productNinetyDayFreebies || ''}`.trim();
  const selectedSellingPlanId = `${data.selectedSellingPlanId || ''}`.trim();
  const addedQuantity = Math.max(1, Number(data.itemCount || 1));

  if (openTimeout) {
    window.clearTimeout(openTimeout);
  }

  openTimeout = window.setTimeout(async () => {
    const drawer = getDrawer();
    if (!drawer) {
      if (hasDeferredCartDrawerOpen()) {
        openDeferredCartDrawer();
      }
      return;
    }
    const { scenario, item, line, currency, productHasNinetyDaySubscription } = await getScenarioFromCart({
      lineItemKey,
      variantId,
      productId,
      selectedSellingPlanId,
      addedQuantity,
    });
    if (productHasNinetyDaySubscription) {
      if (hasDeferredCartDrawerOpen()) {
        openDeferredCartDrawer();
      }
      return;
    }
    if (!isAllowedDrawerScenario(drawer, scenario)) {
      if (hasDeferredCartDrawerOpen()) {
        openDeferredCartDrawer();
      }
      return;
    }

    latestScenarioContext = {
      item,
      line,
      sourceElement: event.target instanceof Element ? event.target : null,
    };

    updateDrawerTitle(drawer, scenario, item, productCustomTitle);
    if (!itemSupportsSubscription(item, line)) {
      if (hasDeferredCartDrawerOpen()) {
        openDeferredCartDrawer();
      }
      return;
    }

    setActiveScenario(drawer, scenario);
    updateThirtyDayFreebies(drawer, scenario, productNinetyDayFreebies);
    await updateNinetyDayActions(drawer, scenario, item, currency);
    updateUpgradeToNinetyActions(drawer, scenario, item, line);

    const cartDrawerComponent = getCartDrawerComponent();
    const cartDrawerDialog = document.querySelector('cart-drawer-component dialog[open]');

    if (cartDrawerDialog instanceof HTMLDialogElement) {
      if (cartDrawerComponent && 'closeDialog' in cartDrawerComponent && typeof cartDrawerComponent.closeDialog === 'function') {
        await cartDrawerComponent.closeDialog();
      } else if (cartDrawerComponent && 'close' in cartDrawerComponent && typeof cartDrawerComponent.close === 'function') {
        cartDrawerComponent.close();
        await waitForDialogClose(cartDrawerDialog);
      } else {
        await waitForDialogClose(cartDrawerDialog);
      }
    }

    shouldOpenCartDrawerAfterClose = hasDeferredCartDrawerOpen();
    openDrawer(drawer);
  }, OPEN_DELAY_MS);
});

document.addEventListener('click', (event) => {
  const actionTarget = event.target instanceof Element ? event.target.closest(ACTION_SELECTOR) : null;
  if (!(actionTarget instanceof HTMLElement)) return;

  const action = actionTarget.dataset.cartSubscriptionPlanAction;

  if (action === CHECKOUT_ACTION) {
    window.location.assign(CHECKOUT_URL);
    return;
  }

  if (action === ADD_ONE_TIME_ACTION && actionTarget instanceof HTMLButtonElement) {
    addOneTimePurchase(actionTarget);
    return;
  }

  if (action === UPDATE_TO_90_ACTION && actionTarget instanceof HTMLButtonElement) {
    updateToNinetyDaySubscription(actionTarget);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest(CLOSE_SELECTOR) : null;
  if (!target) return;

  const drawer = target.closest(DRAWER_SELECTOR);
  const resolvedDrawer = drawer instanceof HTMLElement ? drawer : getDrawer();
  const shouldOpenCart = shouldOpenCartDrawerAfterClose || hasDeferredCartDrawerOpen();
  if (shouldOpenCart) {
    closeSubscriptionDrawerAndOpenCart(resolvedDrawer);
    return;
  }
  closeDrawer(resolvedDrawer);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const drawer = getDrawer();
  const shouldOpenCart = shouldOpenCartDrawerAfterClose || hasDeferredCartDrawerOpen();
  if (shouldOpenCart) {
    closeSubscriptionDrawerAndOpenCart(drawer);
    return;
  }
  closeDrawer(drawer);
});
